'use client';

import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { twMerge } from 'tailwind-merge';
import { CategoryTypeId } from '@diritto/common/content/constants';
import { Color } from '@diritto/common/style/color';
import { TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { ButtonType } from 'constants/button';
import { isPC } from 'constants/layout';
import { ModalParams, ModalType } from 'constants/modal';
import { Button } from 'ui-components/common/Button';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { useMedia } from 'ui-hooks/useMediaQuery';
import { useShowModal } from 'ui-hooks/useShowModal';
import { ModalHandler } from 'utils/ModalHandler';
import { showToast } from 'utils/modal';
import { CircleCheckBoxIcon } from 'icons/CircleCheckBoxIcon';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useHomeSettingDispatch } from 'contexts/HomeSettingProvider';

const FavoriteCategoryItemList = [
  {
    title: '모든 장르 볼래요 🎲',
    description: '판타지, BL, 로판, 로맨스, 자유, 팬픽',
    categoryIds: [
      CategoryTypeId.Fantasy,
      CategoryTypeId.BL,
      CategoryTypeId.RomanceFantasy,
      CategoryTypeId.Romance,
      CategoryTypeId.Any,
      CategoryTypeId.FanFiction,
    ],
  },
  {
    title: '로·로·판 위주로 볼래요 ✨',
    description: '로맨스, 로판, 판타지, 자유',
    categoryIds: [CategoryTypeId.Romance, CategoryTypeId.RomanceFantasy, CategoryTypeId.Fantasy, CategoryTypeId.Any],
  },
  {
    title: 'BL만 보고싶어요 👀',
    description: 'BL',
    categoryIds: [CategoryTypeId.BL],
  },
];

const SELECT_FAVORITE_CATEGORY_STYLES = {
  titleContainer: 'flex flex-col justify-center items-center w-full gap-2 mb-[28px]',
  title: 'justify-center items-center w-full',
  titleText: 'text-center text-black text-sm font-bold',
  titleDescription: 'text-gray-600 text-xxs font-medium',
  categoryGuideMessageContainer: 'flex flex-col justify-start items-start gap-0.5',
  categoryGuideMessageImportant: 'flex flex-row items-start w-full gap-1 text-violet-500',
  categoryGuideMessageNormal: 'flex flex-row items-start w-full gap-1 text-gray-500',
  buttonContainer: 'flex flex-col justify-center items-center w-full mt-8 gap-1',
};

export const SelectFavoriteCategoryModal = () => {
  const { tracker } = useTrackingState();
  const { isPc } = useMedia();
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const { changeCategoryIds: changeFavoriteCategoryIds } = useHomeSettingDispatch();

  const [state, setState] = useState<ModalParams[ModalType.SelectFavoriteCategory]>();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleClickFavoriteCategoryItem = useCallback(
    (index: number) => {
      setSelectedIndex(index);
    },
    [setSelectedIndex],
  );

  const handleHide = useCallback(() => {
    hideModal();
  }, [hideModal]);

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    handleHide();
  }, [handleHide, state]);

  const handleSubmit = useCallback(async () => {
    if (selectedIndex === null) {
      handleHide();
      return;
    }

    const selectedCategoryIds = FavoriteCategoryItemList[selectedIndex].categoryIds ?? [];

    tracker.track(TrackingEventType.ClickProfileSettingFavoriteCategory, {
      categoryIds: selectedCategoryIds,
      trackerExtraInfo: {
        trackerPosition: TrackerPosition.Home,
      },
    });

    changeFavoriteCategoryIds(selectedCategoryIds);

    showToast('선호 장르가 적용되었어요.');
    state?.onConfirm?.();
    handleHide();
  }, [state, handleHide, selectedIndex, tracker, changeFavoriteCategoryIds]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.SelectFavoriteCategory]) => {
      setState(params);

      if (show) {
        showModal();
      } else {
        handleHide();
      }
    };

    ModalHandler.addEventListener(ModalType.SelectFavoriteCategory, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.SelectFavoriteCategory);
    };
  }, [handleHide, showModal]);

  const renderFavoriteCategoryItem = useCallback(
    (item: { title: string; description: string }, index: number) => (
      <div
        key={`favorite-category-item-${index}`}
        className={twMerge(
          'flex flex-row h-[78px] w-full p-4 gap-5 rounded-md outline outline-1 outline-offset-[-1px] overflow-hidden cursor-pointer',
          selectedIndex === index ? 'outline-violet-500 bg-violet-50' : 'outline-black/10',
        )}
        onClick={() => handleClickFavoriteCategoryItem(index)}>
        <div className="flex justify-center items-center">
          <CircleCheckBoxIcon checked={selectedIndex === index} size={20} />
        </div>
        <div className="flex flex-col justify-start items-start gap-1">
          <p className="justify-start text-black text-xs font-bold">{item.title}</p>
          <p className="justify-start text-gray-500 text-xxxs font-normal">{item.description}</p>
        </div>
      </div>
    ),
    [handleClickFavoriteCategoryItem, selectedIndex],
  );

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal} isBottom={!isPc}>
      <StyledModal>
        <div className={SELECT_FAVORITE_CATEGORY_STYLES.titleContainer}>
          <div className={SELECT_FAVORITE_CATEGORY_STYLES.title}>
            <p className={SELECT_FAVORITE_CATEGORY_STYLES.titleText}>내 취향에 맞게 시작해볼까요?</p>
          </div>
          <p className={SELECT_FAVORITE_CATEGORY_STYLES.titleDescription}>
            선택한 장르에 맞춰 콘텐츠를 구성해드릴게요.
          </p>
        </div>

        <div className="flex flex-col justify-start items-start w-full gap-2.5">
          {FavoriteCategoryItemList.map(renderFavoriteCategoryItem)}

          <div className={SELECT_FAVORITE_CATEGORY_STYLES.categoryGuideMessageContainer}>
            <div className={SELECT_FAVORITE_CATEGORY_STYLES.categoryGuideMessageImportant}>
              <div className="items-center justify-start text-xxxs font-medium">·</div>
              <p className="flex text-xxxs font-medium">선호 장르는 퀵메뉴에서 언제든 변경할 수 있어요.</p>
            </div>
            <div className={SELECT_FAVORITE_CATEGORY_STYLES.categoryGuideMessageNormal}>
              <div className="items-center justify-start text-xxxs font-medium">·</div>
              <p className="flex text-xxxs font-medium">일부 콘텐츠 및 배너에는 적용되지 않을 수 있어요.</p>
            </div>
          </div>
        </div>

        <div className={SELECT_FAVORITE_CATEGORY_STYLES.buttonContainer}>
          <StyledButton label="적용하기" onClick={handleSubmit} disabled={selectedIndex === null} />
          <StyledButton label="닫기" buttonType={ButtonType.SecondaryNoBorder} onClick={handleClose} />
        </div>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledModal = styled.div`
  width: 100vw;
  border-radius: 16px 16px 0 0;
  padding: 32px 24px 0 24px;

  @media (${isPC}) {
    width: 400px;
    border-radius: 16px;
    padding: 32px 24px 16px 24px;
  }
  background-color: ${Color.White};

  display: flex;
  flex-direction: column;
  align-items: center;
  ${SafeAreaBottomStyles(16)}
`;

const StyledButton = styled(Button)`
  width: 100%;
`;
